import { useState, useEffect, useRef, useCallback } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { ProjectMedia, PresignedUrlResponse } from "@/types";
import { fetchMediaPresignedUrl } from "@/services/api";
import { useIsMobile } from "@/hooks/use-mobile";
import { Spinner } from "@/components/ui/spinner";
import { ImageOff, PlayIcon } from "lucide-react";

interface ProjectMediaThumbnailSliderProps {
  projectId: string;
  media: ProjectMedia[];
  onImageClick: (index: number) => void;
}

const ProjectMediaThumbnailSlider = ({
  projectId,
  media,
  onImageClick,
}: ProjectMediaThumbnailSliderProps) => {
  const [presignedUrls, setPresignedUrls] = useState<
    Record<string, PresignedUrlResponse>
  >({});
  const [loadingUris, setLoadingUris] = useState<Record<string, boolean>>({});
  const presignedUrlsRef = useRef<Record<string, PresignedUrlResponse>>({});
  const loadingUrisRef = useRef<Record<string, boolean>>({});
  const observers = useRef<Record<string, IntersectionObserver>>({});
  const isMobile = useIsMobile();

  useEffect(() => {
    presignedUrlsRef.current = presignedUrls;
  }, [presignedUrls]);

  useEffect(() => {
    loadingUrisRef.current = loadingUris;
  }, [loadingUris]);

  const fetchPresignedUrl = useCallback(async (uri: string) => {
    if (presignedUrlsRef.current[uri] || loadingUrisRef.current[uri]) return;

    setLoadingUris((prev) => ({ ...prev, [uri]: true }));

    try {
      const result = await fetchMediaPresignedUrl(projectId, uri);
      if (result) {
        setPresignedUrls((prev) => ({ ...prev, [uri]: result }));
      }
    } catch (error) {
      console.error(`Error loading presigned URL for ${uri}:`, error);
    } finally {
      setLoadingUris((prev) => ({ ...prev, [uri]: false }));
    }
  }, [projectId]);

  // Set up intersection observer to track visible items
  useEffect(() => {
    // Clean up previous observers
    Object.values(observers.current).forEach((observer) =>
      observer.disconnect(),
    );
    observers.current = {};

    // Create new observers for each item
    media.forEach((item) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              fetchPresignedUrl(item.uri);
            }
          });
        },
        { threshold: 0.1, rootMargin: "100px" }, // Load images when they're close to viewport
      );

      observers.current[item.uri] = observer;
    });

    return () => {
      // Clean up on unmount
      Object.values(observers.current).forEach((observer) =>
        observer.disconnect(),
      );
      observers.current = {};
    };
  }, [media, fetchPresignedUrl]);

  // Prefetch just the first image on mount regardless of visibility
  useEffect(() => {
    if (media.length > 0) {
      fetchPresignedUrl(media[0].uri);
    }
  }, [media, fetchPresignedUrl]);

  if (!media.length) {
    return null;
  }

  return (
    <div className="relative w-full mt-4">
      <Carousel
        opts={{
          align: "start",
          loop: media.length > 4,
        }}
        className="w-full"
      >
        <CarouselContent>
          {media.map((item, index) => (
            <CarouselItem
              key={item.uri}
              className="basis-1/4 sm:basis-1/5 md:basis-1/6 lg:basis-1/8 cursor-pointer"
              ref={(el) => {
                if (el && observers.current[item.uri]) {
                  observers.current[item.uri].observe(el);
                }
              }}
            >
              <div
                className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-primary transition-all"
                onClick={() => {
                  fetchPresignedUrl(item.uri);
                  onImageClick(index);
                }}
                onMouseEnter={() => fetchPresignedUrl(item.uri)}
              >
                {presignedUrls[item.uri] ? (
                  <>
                    {item.fileName.endsWith(".mp4") ? (
                      <>
                        <video
                          src={presignedUrls[item.uri]?.downloadUrl}
                          controls={false} // No controls for thumbnails
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <button
                            className="bg-black bg-opacity-50 text-white rounded-full p-2"
                            onClick={() => onImageClick(index)}
                          >
                            <PlayIcon className="h-6 w-6" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <img
                        src={presignedUrls[item.uri].thumbnailDownloadUrl}
                        alt={item.fileName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </>
                ) : loadingUris[item.uri] ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <Spinner className="h-6 w-6" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <ImageOff className="h-6 w-6 text-gray-400" />
                  </div>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {media.length > 4 && (
          <>
            <CarouselPrevious
              className={`left-2 ${isMobile ? "w-7 h-7" : ""}`}
            />
            <CarouselNext className={`right-2 ${isMobile ? "w-7 h-7" : ""}`} />
          </>
        )}
      </Carousel>
    </div>
  );
};

export default ProjectMediaThumbnailSlider;
